import { Box, Button, Flex, HStack, Tag, TagCloseButton, TagLabel, Text } from "@chakra-ui/react";
import { ChartBar, DotsThreeVertical } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import LocalSpinner from "../../../../components/LocalSpinner";

export const SCOPE_COLORS = {
  scope1: { base: "#25C469", light: "#B2F1DE", text: "#0B5E30" },
  scope2: { base: "#5EB5F3", light: "#BFE0FA", text: "#1A5276" },
  scope3: { base: "#9A34F3", light: "#D8B4FE", text: "#5B1D99" },
};

// Shared tooltip — same shape as the WasteFlowSankey / EmissionsTree /
// EmissionsIcicle tooltips so the hover language is consistent across
// the Insights surface. Caller provides `position` (relative to the
// chart container) plus a title + dot + value/footnote rows.
const ChartTooltip = ({ position, title, rows }) => {
  if (!position) return null;
  const { left, top } = position;
  return (
    <Box
      position="absolute"
      left={`${left}px`}
      top={`${top}px`}
      bg="white"
      px={3}
      py={2}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      boxShadow="md"
      pointerEvents="none"
      fontSize="12px"
      zIndex={20}
      minW="170px"
    >
      <Text fontWeight="700" color="gray.800" mb={rows?.length ? 1 : 0}>
        {title}
      </Text>
      {rows && rows.map((r, i) => (
        <Flex key={i} align="center" gap={2} mt={i === 0 ? 0 : 0.5}>
          {r.dotColor && (
            <Box w="8px" h="8px" borderRadius="sm" bg={r.dotColor} flexShrink={0} />
          )}
          <Text color="gray.600" flex="1">{r.label}</Text>
          {r.value != null && (
            <Text color="gray.800" fontWeight="600" whiteSpace="nowrap">{r.value}</Text>
          )}
        </Flex>
      ))}
    </Box>
  );
};

const fmtTco2e = (v) => `${Number(v || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 })} tCO₂e`;
const fmtPctSigned = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  const arrow = v < 0 ? "↓" : v > 0 ? "↑" : "→";
  return `${arrow} ${Math.abs(v).toFixed(1)} %`;
};

const SCOPE_OPTIONS = [
  { key: "scope1", label: "Scope 1", color: SCOPE_COLORS.scope1 },
  { key: "scope2Location", label: "Scope 2 - Location", color: SCOPE_COLORS.scope2 },
  { key: "scope2Market", label: "Scope 2 - Market", color: SCOPE_COLORS.scope2 },
  { key: "scope1And2Market", label: "Scope 1 & 2 - Market", color: SCOPE_COLORS.scope1 },
  { key: "scope1And2Location", label: "Scope 1 & 2 - Location", color: SCOPE_COLORS.scope1 },
  { key: "scope3", label: "Scope 3", color: SCOPE_COLORS.scope3 },
];

// Which detail page a bar drills into. The combined "Scope 1 & 2" pairs
// intentionally have no target — there's no combined drill-down page.
const DRILL_TARGETS = {
  "Scope 1": "scopeOne",
  "Scope 2 - Location": "scopeTwo",
  "Scope 2 - Market": "scopeTwo",
  "Scope 3": "scopeThree",
};

// Custom paired-bar chart with dual y-axes — Scope 1 / 2 / Totals plot
// against the left axis, Scope 3 plots against its own right axis so the
// two scales (which can differ by ~100×) don't squash the smaller scopes
// into invisibility. Same pair connector + % chip language as the
// lollipop view so the two views feel related.
const SCOPE3_LABELS = new Set(["Scope 3"]);

const PairedBarChart = ({ items, previousYear, currentYear, drillableTypes, onDrill }) => {
  const wrapRef = useRef(null);
  const [wrapWidth, setWrapWidth] = useState(0);
  // Hover tooltip state. `kind` distinguishes the source bar so we can
  // colour the dot in the tooltip to match (light = previous year, base
  // = current year). Position is relative to wrapRef.
  const [hover, setHover] = useState(null);
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 0;
      setWrapWidth(w);
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // Convert the SVG-event clientX/clientY into wrapRef-local coordinates
  // so the absolute-positioned tooltip lines up with the cursor. Offsets
  // the tooltip slightly down-and-right of the cursor; flips left when
  // the cursor is near the right edge so the tooltip stays in-frame.
  const toLocal = (event) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return { left: 0, top: 0 };
    const TIP_W = 200;
    const offset = 12;
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const fitsRight = x + offset + TIP_W <= rect.width;
    return {
      left: fitsRight ? x + offset : Math.max(0, x - offset - TIP_W),
      top: Math.max(0, y + offset),
    };
  };

  const HEIGHT = 460;
  const TOP = 40;
  const BOTTOM = 70;
  const LEFT = 80;
  const RIGHT = 80;
  const VIEW_WIDTH = wrapWidth || 0;

  // Split rows by axis. Anything tagged as Scope 3 plots against the
  // right axis; everything else against the left.
  const leftRows = items.filter((d) => !SCOPE3_LABELS.has(d.type));
  const rightRows = items.filter((d) => SCOPE3_LABELS.has(d.type));

  const leftRange = useMemo(() => buildAxisRange(leftRows, previousYear, currentYear), [leftRows, previousYear, currentYear]);
  const rightRange = useMemo(() => buildAxisRange(rightRows, previousYear, currentYear), [rightRows, previousYear, currentYear]);

  // Each category gets a horizontal slot wide enough for two bars + gap.
  const SLOT_GAP = 18;
  const innerW = Math.max(0, VIEW_WIDTH - LEFT - RIGHT);
  const slotW = items.length > 0 ? innerW / items.length : 0;
  const BAR_W = Math.max(8, Math.min(38, (slotW - SLOT_GAP) / 2));
  const innerH = HEIGHT - TOP - BOTTOM;

  const formatTick = (v) => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}k`;
    return String(Math.round(v));
  };

  const PILL = {
    improvement: { bg: "#F0FFF4", border: "#C6F6D5", text: "#276749" },
    regression: { bg: "#FFF5F5", border: "#FED7D7", text: "#C53030" },
    neutral: { bg: "#F7FAFC", border: "#E2E8F0", text: "#4A5568" },
  };

  const toY = (value, range) => {
    const denom = range.max - range.min || 1;
    return TOP + innerH - ((value - range.min) / denom) * innerH;
  };

  return (
    <Box w="100%" h="100%" ref={wrapRef} position="relative">
      <ChartTooltip position={hover?.position} title={hover?.title} rows={hover?.rows} />
      {VIEW_WIDTH > 0 && (
        <svg
          width="100%"
          height={HEIGHT}
          viewBox={`0 0 ${VIEW_WIDTH} ${HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: "block", overflow: "visible" }}
        >
          {/* Left axis ticks + gridlines */}
          {leftRange.ticks.map((tick, i) => {
            const y = toY(tick, leftRange);
            return (
              <g key={`left-${i}`}>
                <line
                  x1={LEFT}
                  x2={VIEW_WIDTH - RIGHT}
                  y1={y}
                  y2={y}
                  stroke="#E2E8F0"
                  strokeDasharray={i === 0 ? undefined : "3 3"}
                />
                <text
                  x={LEFT - 8}
                  y={y + 4}
                  textAnchor="end"
                  fontSize={11}
                  fill="#718096"
                >
                  {formatTick(tick)}
                </text>
              </g>
            );
          })}

          {/* Right axis ticks (only when Scope 3 is shown) */}
          {rightRows.length > 0 && rightRange.ticks.map((tick, i) => {
            const y = toY(tick, rightRange);
            return (
              <text
                key={`right-${i}`}
                x={VIEW_WIDTH - RIGHT + 8}
                y={y + 4}
                textAnchor="start"
                fontSize={11}
                fill={SCOPE_COLORS.scope3.text}
              >
                {formatTick(tick)}
              </text>
            );
          })}

          {/* Axis labels */}
          <text
            x={20}
            y={(TOP + HEIGHT - BOTTOM) / 2}
            textAnchor="middle"
            transform={`rotate(-90, 20, ${(TOP + HEIGHT - BOTTOM) / 2})`}
            fontSize={12}
            fontWeight={600}
            fill="#2D3748"
          >
            Scope 1, 2 & combined (tCO2e)
          </text>
          {rightRows.length > 0 && (
            <text
              x={VIEW_WIDTH - 20}
              y={(TOP + HEIGHT - BOTTOM) / 2}
              textAnchor="middle"
              transform={`rotate(90, ${VIEW_WIDTH - 20}, ${(TOP + HEIGHT - BOTTOM) / 2})`}
              fontSize={12}
              fontWeight={600}
              fill={SCOPE_COLORS.scope3.text}
            >
              Scope 3 (tCO2e)
            </text>
          )}

          {/* Dashed divider on the left edge of the Scope 3 slot — a
              quiet visual cue that bars to the right read against the
              right axis (which is itself coloured purple). */}
          {(() => {
            const s3Index = items.findIndex((d) => SCOPE3_LABELS.has(d.type));
            if (s3Index < 0) return null;
            const dividerX = LEFT + s3Index * slotW;
            return (
              <line
                x1={dividerX}
                x2={dividerX}
                y1={TOP - 10}
                y2={TOP + innerH + 10}
                stroke={SCOPE_COLORS.scope3.base}
                strokeWidth={1}
                strokeDasharray="3 4"
                opacity={0.6}
              />
            );
          })()}

          {/* Bars + connectors */}
          {items.map((row, i) => {
            const opt = SCOPE_OPTIONS.find((s) => s.label === row.type);
            const color = opt?.color || SCOPE_COLORS.scope1;
            const isRight = SCOPE3_LABELS.has(row.type);
            const range = isRight ? rightRange : leftRange;
            const slotX = LEFT + i * slotW + slotW / 2;
            const xPrev = slotX - BAR_W - SLOT_GAP / 4;
            const xCurr = slotX + SLOT_GAP / 4;
            const prev = Number(row[previousYear] || 0);
            const curr = Number(row[currentYear] || 0);
            const yPrev = toY(prev, range);
            const yCurr = toY(curr, range);
            const baseY = TOP + innerH;
            const pct = prev > 0 ? ((curr - prev) / prev) * 100 : null;
            const variant = pct == null
              ? PILL.neutral
              : pct < 0
                ? PILL.improvement
                : PILL.regression;
            const arrow = pct == null ? "" : pct < 0 ? "↓" : "↑";
            const valueText = pct == null ? "—" : `${Math.abs(pct).toFixed(1)} %`;
            const labelText = arrow ? `${arrow} ${valueText}` : valueText;
            const pillW = Math.max(54, labelText.length * 6.5 + 16);
            // Top of the higher bar — connector tilts to the lower one.
            const topY = Math.min(yPrev, yCurr);
            const pillCx = slotX;
            const pillCy = topY - 16;
            const drillable = drillableTypes?.has(row.type);
            const handleEnter = (kind) => (e) => {
              const barYear = kind === "prev" ? previousYear : currentYear;
              setHover({
                position: toLocal(e),
                title: row.type,
                rows: [
                  {
                    dotColor: color.light,
                    label: `${previousYear}`,
                    value: fmtTco2e(prev),
                    isPrev: true,
                  },
                  {
                    dotColor: color.base,
                    label: `${currentYear}`,
                    value: fmtTco2e(curr),
                    isCurr: true,
                  },
                  {
                    label: "Year-on-year",
                    value: fmtPctSigned(pct),
                    pillColor: variant.text,
                  },
                  ...(drillable
                    ? [{ label: `Click to drill down into ${barYear}` }]
                    : []),
                ],
                kind,
              });
            };
            const handleMove = (e) => setHover((prev0) => prev0 ? { ...prev0, position: toLocal(e) } : prev0);
            const handleLeave = () => setHover(null);
            const handleClick = (barYear) => () => {
              if (drillable) onDrill(row.type, barYear);
            };
            return (
              <g key={`row-${row.type}`}>
                {/* Bars — pointerEvents on rect so the whole pair is hover-
                    able, including the small gap between the two bars. */}
                <rect
                  x={xPrev}
                  y={yPrev}
                  width={BAR_W}
                  height={Math.max(0, baseY - yPrev)}
                  fill={color.light}
                  stroke={color.base}
                  strokeWidth={1}
                  rx={4}
                  onMouseEnter={handleEnter("prev")}
                  onMouseMove={handleMove}
                  onMouseLeave={handleLeave}
                  onClick={handleClick(previousYear)}
                  style={{ cursor: drillable ? "pointer" : "default" }}
                />
                <rect
                  x={xCurr}
                  y={yCurr}
                  width={BAR_W}
                  height={Math.max(0, baseY - yCurr)}
                  fill={color.base}
                  rx={4}
                  onMouseEnter={handleEnter("curr")}
                  onMouseMove={handleMove}
                  onMouseLeave={handleLeave}
                  onClick={handleClick(currentYear)}
                  style={{ cursor: drillable ? "pointer" : "default" }}
                />
                {/* Year labels in-bar (top of bar). pointerEvents none so
                    the label doesn't swallow the bar's click/hover. */}
                {baseY - yPrev >= 18 && (
                  <text
                    x={xPrev + BAR_W / 2}
                    y={yPrev + 12}
                    textAnchor="middle"
                    fontSize={10}
                    fontWeight={700}
                    fill={color.text}
                    pointerEvents="none"
                  >
                    {previousYear}
                  </text>
                )}
                {baseY - yCurr >= 18 && (
                  <text
                    x={xCurr + BAR_W / 2}
                    y={yCurr + 12}
                    textAnchor="middle"
                    fontSize={10}
                    fontWeight={700}
                    fill="white"
                    pointerEvents="none"
                  >
                    {currentYear}
                  </text>
                )}
                {/* Connector + % chip */}
                <line
                  x1={xPrev + BAR_W / 2}
                  y1={yPrev}
                  x2={xCurr + BAR_W / 2}
                  y2={yCurr}
                  stroke={variant.text}
                  strokeWidth={1.25}
                  strokeDasharray="4 3"
                  opacity={0.7}
                />
                <rect
                  x={pillCx - pillW / 2}
                  y={pillCy - 9}
                  width={pillW}
                  height={18}
                  rx={9}
                  ry={9}
                  fill={variant.bg}
                  stroke={variant.border}
                  strokeWidth={1}
                />
                <text
                  x={pillCx}
                  y={pillCy + 4}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill={variant.text}
                >
                  {labelText}
                </text>
                {/* Category label below */}
                <text
                  x={slotX}
                  y={baseY + 22}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill="#2D3748"
                >
                  {row.type}
                </text>
              </g>
            );
          })}
        </svg>
      )}
    </Box>
  );
};

// Build a 5-tick "nice" axis range hugging the data. Shared by both axes.
function buildAxisRange(rows, previousYear, currentYear) {
  const values = rows.flatMap((r) => [Number(r[previousYear] || 0), Number(r[currentYear] || 0)])
    .filter((v) => Number.isFinite(v));
  if (values.length === 0) {
    return { min: 0, max: 1, ticks: [0, 0.25, 0.5, 0.75, 1] };
  }
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const TICK_COUNT = 5;
  const span = Math.max(1, rawMax - rawMin);
  const niceStepCeil = (s) => {
    const pow = Math.pow(10, Math.floor(Math.log10(s)));
    const norm = s / pow;
    let mult;
    if (norm <= 1) mult = 1;
    else if (norm <= 2) mult = 2;
    else if (norm <= 5) mult = 5;
    else mult = 10;
    return mult * pow;
  };
  let step = niceStepCeil(span / (TICK_COUNT - 1));
  let axisMin = Math.max(0, Math.floor(rawMin / step) * step);
  while (axisMin + step * (TICK_COUNT - 1) < rawMax) {
    step = niceStepCeil(step * 1.5);
    axisMin = Math.max(0, Math.floor(rawMin / step) * step);
  }
  const axisMax = axisMin + step * (TICK_COUNT - 1);
  return {
    min: axisMin,
    max: axisMax,
    ticks: Array.from({ length: TICK_COUNT }, (_, i) => axisMin + step * i),
  };
}

// Pill toggle to flip between the bar pair-up view and the lollipop /
// Cleveland dot view. Mirrors the Summary / Visual ToggleButton at the
// page level so the interaction looks consistent.
const ViewToggle = ({ value, onChange }) => {
  const options = [
    { value: "bars", label: "Bars", icon: <ChartBar size={16} /> },
    { value: "lollipop", label: "Lollipop", icon: <DotsThreeVertical size={16} /> },
  ];
  return (
    <Flex bg="gray.100" borderRadius="full" p="3px">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <Button
            key={opt.value}
            size="xs"
            borderRadius="full"
            bg={active ? "pureBlack" : "transparent"}
            color={active ? "white" : "gray.500"}
            fontWeight={active ? "600" : "normal"}
            rightIcon={opt.icon}
            onClick={() => onChange(opt.value)}
            _hover={{ bg: active ? "pureBlack" : "gray.200" }}
            px={4}
          >
            {opt.label}
          </Button>
        );
      })}
    </Flex>
  );
};

// Cleveland dot / lollipop chart — one row per category, value on the
// x-axis. Two dots per row (previous and current year) connected by a
// segment so the year-over-year direction reads at a glance with much
// less visual clutter than a paired bar chart. Uses the existing
// SCOPE_OPTIONS palette and the same arrow / pill styling as the bar
// connector pills. Groups must be small (3 max) so a single group's
// niceMax keeps the dots well-separated on its own scale.
const LollipopPanel = ({ items, previousYear, currentYear, width }) => {
  const wrapRef = useRef(null);
  // Hover tooltip state for the lollipop rows. Same shape as the bar-
  // chart's so ChartTooltip can render either without branching.
  const [hover, setHover] = useState(null);
  const toLocal = (event) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return { left: 0, top: 0 };
    const TIP_W = 200;
    const offset = 12;
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const fitsRight = x + offset + TIP_W <= rect.width;
    return {
      left: fitsRight ? x + offset : Math.max(0, x - offset - TIP_W),
      top: Math.max(0, y + offset),
    };
  };
  // Compact vertical sizing so all 4 panels fit inside the 500px parent
  // without a scrollbar even with every scope toggle on.
  const ROW_HEIGHT = 38;
  const TOP = 18;
  const BOTTOM = 22;
  // LEFT must be wide enough for the longest scope label ("Scope 1 & 2 -
  // Location" at 12px ≈ 165px). RIGHT leaves room for the % delta chip.
  const LEFT = 180;
  const RIGHT = 100;
  // viewBox width = measured container width so the SVG renders at 1:1
  // pixel scale (no font / dot stretching). Falls back to a sensible
  // minimum if the container is briefly tiny during first layout.
  const VIEW_WIDTH = width;

  const valueRows = useMemo(() => {
    return items.map((d) => {
      const opt = SCOPE_OPTIONS.find((s) => s.label === d.type);
      const color = opt?.color || SCOPE_COLORS.scope1;
      const prev = Number(d[previousYear] || 0);
      const curr = Number(d[currentYear] || 0);
      const pct = prev > 0 ? ((curr - prev) / prev) * 100 : null;
      return { label: d.type, color, prev, curr, pct };
    });
  }, [items, previousYear, currentYear]);

  if (valueRows.length === 0) return null;

  // Each panel uses an x-domain that hugs its own data so the dots fill the
  // available space — Scope 3 may be 100× the size of Scope 1, but within
  // each panel we want the previous/current dots to sit well-apart.
  // Algorithm:
  //   - rawMin / rawMax: actual smallest and largest values in this panel
  //   - axisMin: floored to a "nice" round number ≤ rawMin (or 0 if the
  //     range is small relative to the floor — keeps the scale honest
  //     when values are close to zero)
  //   - axisMax: ceiled to a nice number ≥ rawMax
  //   - 5 evenly-spaced ticks across [axisMin, axisMax]
  const rawValues = valueRows.flatMap((r) => [r.prev, r.curr]).filter((v) => Number.isFinite(v));
  const rawMin = rawValues.length ? Math.min(...rawValues) : 0;
  const rawMax = rawValues.length ? Math.max(...rawValues) : 1;

  // Always plot the same number of evenly-spaced ticks across every
  // panel so the gridlines line up vertically when the panels are
  // stacked. We hug the data range with a "nice" round step so the
  // numbers still read cleanly (1, 2, 5, 10, 20, 50… × 10ⁿ).
  const TICK_COUNT = 5;
  const span = Math.max(1, rawMax - rawMin);
  // Round step UP to the next nice value so that step × (TICK_COUNT - 1)
  // is always at least as wide as the data range.
  const niceStepCeil = (s) => {
    const pow = Math.pow(10, Math.floor(Math.log10(s)));
    const norm = s / pow;
    let mult;
    if (norm <= 1) mult = 1;
    else if (norm <= 2) mult = 2;
    else if (norm <= 5) mult = 5;
    else mult = 10;
    return mult * pow;
  };
  let step = niceStepCeil(span / (TICK_COUNT - 1));
  let axisMin = Math.max(0, Math.floor(rawMin / step) * step);
  // axisMin floored down may push the data partly out of axisMin..axisMax;
  // if so, bump the step until the chosen tick count actually covers
  // [rawMin, rawMax].
  while (axisMin + step * (TICK_COUNT - 1) < rawMax) {
    step = niceStepCeil(step * 1.0001 * 1.5);
    axisMin = Math.max(0, Math.floor(rawMin / step) * step);
  }
  const finalStep = step;
  const finalAxisMax = axisMin + finalStep * (TICK_COUNT - 1);
  const tickValues = Array.from({ length: TICK_COUNT }, (_, i) => axisMin + finalStep * i);
  const formatTick = (v) => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}k`;
    return String(Math.round(v));
  };

  const PILL = {
    improvement: { bg: "#F0FFF4", border: "#C6F6D5", text: "#276749" },
    regression: { bg: "#FFF5F5", border: "#FED7D7", text: "#C53030" },
    neutral: { bg: "#F7FAFC", border: "#E2E8F0", text: "#4A5568" },
  };

  const height = TOP + valueRows.length * ROW_HEIGHT + BOTTOM;

  if (!width) return null;

  return (
    <Box w="100%" ref={wrapRef} position="relative">
      <ChartTooltip position={hover?.position} title={hover?.title} rows={hover?.rows} />
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block", overflow: "visible" }}
      >
        {/* Vertical gridlines + axis labels */}
        {tickValues.map((tick, i) => {
          const denom = finalAxisMax - axisMin || 1;
          const x = LEFT + ((tick - axisMin) / denom) * (VIEW_WIDTH - LEFT - RIGHT);
          return (
            <g key={`tick-${i}`}>
              <line
                x1={x}
                x2={x}
                y1={TOP - 6}
                y2={TOP + valueRows.length * ROW_HEIGHT}
                stroke="#E2E8F0"
                strokeWidth={1}
                strokeDasharray={i === 0 ? undefined : "3 3"}
              />
              <text
                x={x}
                y={TOP + valueRows.length * ROW_HEIGHT + 18}
                textAnchor="middle"
                fontSize={11}
                fill="#718096"
              >
                {formatTick(tick)}
              </text>
            </g>
          );
        })}
        {/* Rows */}
        {valueRows.map((row, i) => {
          const y = TOP + i * ROW_HEIGHT + ROW_HEIGHT / 2;
          const denom = finalAxisMax - axisMin || 1;
          const xPrev = LEFT + ((row.prev - axisMin) / denom) * (VIEW_WIDTH - LEFT - RIGHT);
          const xCurr = LEFT + ((row.curr - axisMin) / denom) * (VIEW_WIDTH - LEFT - RIGHT);
          const isImprovement = row.pct != null && row.pct < 0;
          const isRegression = row.pct != null && row.pct > 0;
          const variant = isImprovement
            ? PILL.improvement
            : isRegression
              ? PILL.regression
              : PILL.neutral;
          const arrow = row.pct == null ? "" : row.pct < 0 ? "↓" : "↑";
          const valueText = row.pct == null ? "—" : `${Math.abs(row.pct).toFixed(1)} %`;
          const labelText = arrow ? `${arrow} ${valueText}` : valueText;
          const pillW = Math.max(58, labelText.length * 6.5 + 18);
          // Centre the chip on the midpoint of the connector line and sit
          // it just above the line so it reads as the delta between the
          // two dots.
          const pillCx = (xPrev + xCurr) / 2;
          const pillCy = y - 14;
          const handleEnter = (e) => {
            setHover({
              position: toLocal(e),
              title: row.label,
              rows: [
                {
                  dotColor: row.color.light,
                  label: `${previousYear}`,
                  value: fmtTco2e(row.prev),
                },
                {
                  dotColor: row.color.base,
                  label: `${currentYear}`,
                  value: fmtTco2e(row.curr),
                },
                {
                  label: "Year-on-year",
                  value: fmtPctSigned(row.pct),
                },
              ],
            });
          };
          const handleMove = (e) => setHover((prev0) => prev0 ? { ...prev0, position: toLocal(e) } : prev0);
          const handleLeave = () => setHover(null);
          return (
            <g
              key={`row-${i}`}
              onMouseEnter={handleEnter}
              onMouseMove={handleMove}
              onMouseLeave={handleLeave}
              style={{ cursor: "default" }}
            >
              {/* Invisible row-wide hit target so the whole row is hover-
                  able, not just the dots. Sits behind everything else. */}
              <rect
                x={0}
                y={y - ROW_HEIGHT / 2}
                width={VIEW_WIDTH}
                height={ROW_HEIGHT}
                fill="transparent"
              />
              <text
                x={LEFT - 12}
                y={y + 4}
                textAnchor="end"
                fontSize={12}
                fontWeight={600}
                fill="#2D3748"
              >
                {row.label}
              </text>
              <line
                x1={xPrev}
                y1={y}
                x2={xCurr}
                y2={y}
                stroke={row.color.base}
                strokeWidth={2}
                opacity={0.55}
              />
              <circle
                cx={xPrev}
                cy={y}
                r={6}
                fill={row.color.light}
                stroke={row.color.base}
                strokeWidth={1.5}
              />
              <circle cx={xCurr} cy={y} r={6} fill={row.color.base} />
              <text
                x={xPrev}
                y={y + 14}
                textAnchor="middle"
                fontSize={9}
                fontWeight={700}
                fill={row.color.text}
              >
                {previousYear}
              </text>
              <text
                x={xCurr}
                y={y + 14}
                textAnchor="middle"
                fontSize={9}
                fontWeight={700}
                fill={row.color.text}
              >
                {currentYear}
              </text>
              <rect
                x={pillCx - pillW / 2}
                y={pillCy - 9}
                width={pillW}
                height={18}
                rx={9}
                ry={9}
                fill={variant.bg}
                stroke={variant.border}
                strokeWidth={1}
              />
              <text
                x={pillCx}
                y={pillCy + 4}
                textAnchor="middle"
                fontSize={11}
                fontWeight={600}
                fill={variant.text}
              >
                {labelText}
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
};

// Splits the lollipop view into three panels — Scope 1, Scope 2, Scope 3 —
// each with its own x-axis scale so the values stay well-separated within
// the panel even though they vary by an order of magnitude across scopes.
// Stacks vertically on narrow viewports and side-by-side on wider ones via
// a responsive grid wrapper supplied by the caller.
const SCOPE_GROUPS = [
  {
    key: "scope1",
    title: "Scope 1",
    labels: ["Scope 1"],
  },
  {
    key: "scope2",
    title: "Scope 2",
    labels: ["Scope 2 - Location", "Scope 2 - Market"],
  },
  {
    key: "scope1And2",
    title: "Scope 1 & 2",
    labels: ["Scope 1 & 2 - Market", "Scope 1 & 2 - Location"],
  },
  {
    key: "scope3",
    title: "Scope 3",
    labels: ["Scope 3"],
  },
];

const LollipopChart = ({ items, previousYear, currentYear }) => {
  const groups = SCOPE_GROUPS
    .map((g) => ({
      ...g,
      rows: items.filter((d) => g.labels.includes(d.type)),
    }))
    .filter((g) => g.rows.length > 0);

  // Measure the wrapper so we can render each panel's SVG at a real
  // pixel width — this keeps text and dots crisp at their intended size
  // instead of being stretched up by `width: 100%` on a small viewBox.
  const wrapRef = useRef(null);
  const [wrapWidth, setWrapWidth] = useState(0);
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 0;
      setWrapWidth(w);
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  if (groups.length === 0) {
    return (
      <Flex align="center" justify="center" h="100%" color="gray.400">
        <Text>No emissions data to plot.</Text>
      </Flex>
    );
  }

  // Stack vertically and constrain to 75% of the container width so the
  // dots have room without the chart sprawling across the whole page.
  // The y-axis label on each panel already says which scope it is, so no
  // wrapping cards / titles are needed.
  return (
    <Box w="90%" h="100%" overflow="hidden" ref={wrapRef}>
      <Flex direction="column" gap={0}>
        {groups.map((g) => (
          <LollipopPanel
            key={g.key}
            items={g.rows}
            previousYear={previousYear}
            currentYear={currentYear}
            width={wrapWidth}
          />
        ))}
      </Flex>
      <Text
        textAlign="center"
        fontSize="12px"
        fontWeight="600"
        color="gray.700"
        mt={1}
      >
        Emissions (tCO2e)
      </Text>
    </Box>
  );
};

const KpiCharts = ({
  currentYearData,
  previousYearData,
  isFetchingCurrentYear,
  isFetchingPreviousYear,
  selectedYear,
  setYear,
}) => {
  const [visibleScopes, setVisibleScopes] = useState(
    () => new Set(SCOPE_OPTIONS.map((s) => s.key))
  );
  const [view, setView] = useState("bars");
  const [searchParams, setSearchParams] = useSearchParams();
  const drillableTypes = useMemo(() => new Set(Object.keys(DRILL_TARGETS)), []);

  // Clicking a bar opens that scope's drill-down page pre-filtered to the
  // bar's year. Year lives in FootPrint state (URL is synced from it), so
  // both must be updated here.
  const handleDrill = (type, barYear) => {
    const target = DRILL_TARGETS[type];
    if (!target) return;
    setYear(barYear);
    const params = new URLSearchParams(searchParams);
    params.set("year", barYear);
    params.set("detailView", target);
    setSearchParams(params);
  };

  const toggleScope = (key) => {
    setVisibleScopes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const filteredData = constructData(currentYearData, previousYearData, selectedYear)
    .filter((d) => {
      const opt = SCOPE_OPTIONS.find((s) => s.label === d.type);
      return opt && visibleScopes.has(opt.key);
    });

  const getBarColor = ({ id, indexValue }) => {
    const opt = SCOPE_OPTIONS.find((s) => s.label === indexValue);
    const scopeColor = opt?.color || SCOPE_COLORS.scope1;
    return String(id) === String(selectedYear) ? scopeColor.base : scopeColor.light;
  };

  return (
    <Flex direction="column" gap={3}>
      <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
      <HStack spacing={2} flexWrap="wrap">
        {SCOPE_OPTIONS.map((s) => {
          const active = visibleScopes.has(s.key);
          return (
            <Tag
              key={s.key}
              size="sm"
              borderRadius="full"
              cursor="pointer"
              border="1px solid"
              borderColor={active ? s.color.base : "gray.300"}
              bg={active ? s.color.light : "transparent"}
              color={active ? s.color.text : "gray.500"}
              fontWeight="600"
              onClick={() => !active && toggleScope(s.key)}
              _hover={{ bg: active ? s.color.light : "gray.50" }}
              transition="all 0.15s"
            >
              <TagLabel>{s.label}</TagLabel>
              {active && (
                <TagCloseButton
                  onClick={(e) => { e.stopPropagation(); toggleScope(s.key); }}
                />
              )}
            </Tag>
          );
        })}
      </HStack>
      <ViewToggle value={view} onChange={setView} />
      </Flex>
      <Box height="500px" width="100%">
        {isFetchingCurrentYear || isFetchingPreviousYear ? (
          <LocalSpinner />
        ) : view === "lollipop" ? (
          <LollipopChart
            items={filteredData}
            previousYear={selectedYear - 1}
            currentYear={selectedYear}
          />
        ) : (
          <PairedBarChart
            items={filteredData}
            previousYear={selectedYear - 1}
            currentYear={selectedYear}
            drillableTypes={drillableTypes}
            onDrill={handleDrill}
          />
        )}
      </Box>
    </Flex>
  );
};

const constructData = (currentYearData, previousYearData, selectedYear) => {
  return [
    {
      type: "Scope 1",
      [selectedYear - 1]: previousYearData?.scope1Total || 0,
      [selectedYear]: currentYearData?.scope1Total || 0,
    },
    {
      type: "Scope 2 - Location",
      [selectedYear - 1]: previousYearData?.scope2LocationTotal || 0,
      [selectedYear]: currentYearData?.scope2LocationTotal || 0,
    },
    {
      type: "Scope 2 - Market",
      [selectedYear - 1]: previousYearData?.scope2MarketTotal || 0,
      [selectedYear]: currentYearData?.scope2MarketTotal || 0,
    },
    {
      type: "Scope 1 & 2 - Market",
      [selectedYear - 1]: previousYearData?.scope1AndScope2MarketTotal || 0,
      [selectedYear]: currentYearData?.scope1AndScope2MarketTotal || 0,
    },
    {
      type: "Scope 1 & 2 - Location",
      [selectedYear - 1]: previousYearData?.scope1AndScope2LocationTotal || 0,
      [selectedYear]: currentYearData?.scope1AndScope2LocationTotal || 0,
    },
    {
      type: "Scope 3",
      [selectedYear - 1]: previousYearData?.scope3Total || 0,
      [selectedYear]: currentYearData?.scope3Total || 0,
    },
  ];
};

export default KpiCharts;
